import type { Department, Place } from '$lib/api/types';
import { CUSTOM_PLACE_COLORS, PLACE_COLORS, tint } from './palette';

export const BUILT_IN_PLACES = ['fryser', 'koel', 'skab', 'viktualierum'] as const;

export type BuiltInPlace = (typeof BUILT_IN_PLACES)[number];

/** A storage place as the app shows it: built-in places get their name and colour here, own places bring theirs. */
export type PlaceView = {
	key: string;
	name: string;
	color: string;
	/** "i køleskabet" */
	inText: string;
	/** "fra køleskabet" */
	fromText: string;
	enabled: boolean;
	custom: boolean;
};

/** The place messages a view needs; the message catalogue provides them. */
export type PlaceMessages = {
	places: Record<BuiltInPlace, string>;
	placeIn: Record<BuiltInPlace, string>;
	placeFrom: Record<BuiltInPlace, string>;
	ownPlaceIn: (name: string) => string;
	ownPlaceFrom: (name: string) => string;
};

/** Shown for a key the household no longer has (an own place that was removed). */
const UNKNOWN_COLOR = '#E4DDD0';

export function isBuiltIn(key: string): key is BuiltInPlace {
	return (BUILT_IN_PLACES as readonly string[]).includes(key);
}

export function placeView(place: Place, t: PlaceMessages): PlaceView {
	if (isBuiltIn(place.key)) {
		return {
			key: place.key,
			name: t.places[place.key],
			color: PLACE_COLORS[place.key],
			inText: t.placeIn[place.key],
			fromText: t.placeFrom[place.key],
			enabled: place.enabled,
			custom: false
		};
	}
	const name = place.name ?? place.key;
	return {
		key: place.key,
		name,
		color: place.color ?? UNKNOWN_COLOR,
		inText: t.ownPlaceIn(name),
		fromText: t.ownPlaceFrom(name),
		enabled: place.enabled,
		custom: true
	};
}

/** Finds a place by key among the household's places; an unknown key still gets a neutral view. */
export function placeOf(places: readonly PlaceView[], key: string, t: PlaceMessages): PlaceView {
	return (
		places.find((p) => p.key === key) ??
		placeView({ key, name: isBuiltIn(key) ? null : key, color: null, enabled: false }, t)
	);
}

/** Cycle used by the place pill: each usable place in order → not kept in the pantry → the first place. */
export function nextPlace(usable: readonly PlaceView[], place: string | null): string | null {
	const index = place === null ? -1 : usable.findIndex((p) => p.key === place);
	return usable[index + 1]?.key ?? null;
}

/**
 * Where a bought item usually goes, mirroring the backend's rule; null when that place is switched off, and for a
 * household's own department. Pass the item's default department, never where a store shows it.
 */
export function defaultPlaceFor(
	usable: readonly PlaceView[],
	department: Department
): string | null {
	const preferred = preferredPlace(department);
	return preferred && usable.some((p) => p.key === preferred) ? preferred : null;
}

function preferredPlace(department: Department): BuiltInPlace | null {
	if (department === 'frost') return 'fryser';
	if (department === 'mejeri' || department === 'kod') return 'koel';
	if (department === 'gron' || department === 'brod' || department === 'kolonial') return 'skab';
	return null;
}

/** The tile glyph of a meal eaten from a place: snow for the freezer, the stock house elsewhere. */
export function placeGlyph(key: string): string {
	return key === 'fryser' ? '❄' : '⌂';
}

/** A place's colour at 15 %, for boxes and footers about what is stored there. */
export function placeTint(place: PlaceView): string {
	return tint(place.color, 15);
}

/** A place being chosen (in the setup flow or the places dialog); a new own place has no key until it is saved. */
export type PlaceDraft = Pick<Place, 'name' | 'color' | 'enabled'> & { key: string | null };

export function draftView(draft: PlaceDraft, t: PlaceMessages): PlaceView {
	return placeView({ ...draft, key: draft.key ?? '' }, t);
}

/** Adds an own place, switched on and coloured in turn; null when the name is empty or already taken. */
export function addOwnPlace(
	drafts: readonly PlaceDraft[],
	name: string,
	t: PlaceMessages
): PlaceDraft[] | null {
	const trimmed = name.trim();
	const key = trimmed.toLocaleLowerCase();
	if (!trimmed || drafts.some((d) => draftView(d, t).name.toLocaleLowerCase() === key)) return null;
	const ownCount = drafts.filter((d) => d.key === null || !isBuiltIn(d.key)).length;
	const color = CUSTOM_PLACE_COLORS[ownCount % CUSTOM_PLACE_COLORS.length];
	return [...drafts, { key: null, name: trimmed, color, enabled: true }];
}
